import { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import Select, { MultiValue } from 'react-select'

import { useGetIglooBuildingDevices } from '@/api/keyless/igloo/get-igloo-building-devices'
import { useGetIglooStaffConnection } from '@/api/keyless/igloo/get-igloo-staff-connection'
import { useGetIglooStaffDevices } from '@/api/keyless/igloo/get-igloo-staff-devices'
import {
  getDoorFailureCopy,
  getFailedDoorIds,
  getIglooStaffErrorMessage,
} from '@/api/keyless/igloo/igloo-staff-errors'
import {
  getHeldDoorIds,
  getShortestExpiry,
  IglooBuildingDevice,
} from '@/api/keyless/igloo/normalize-igloo-staff-devices'
import { useUpdateIglooStaffDevices } from '@/api/keyless/igloo/update-igloo-staff-devices'
import { toastFactory } from '@/factories'

import Loader from '../../Loader/Loader'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import { saltoSpaceDropdownStyles } from './helpers'

const MAX_CREDENTIAL_DAYS = 365
const DAY_MS = 24 * 60 * 60 * 1000

interface IglooWorkStaffSectionProps {
  isAvailable: boolean
  buildingId?: number
  /** The GLOBAL User id (managementUserData.user.id), not the role-table row id. */
  userId?: number
  staffName: string
}

const IglooWorkStaffSection = ({
  isAvailable,
  buildingId,
  userId,
  staffName,
}: IglooWorkStaffSectionProps) => {
  const buildingIdParam = buildingId ? String(buildingId) : ''
  const userIdParam = userId ? String(userId) : ''

  // Every hook runs unconditionally; `isAvailable` gates the queries via `enabled` and the render
  // guard sits below them. useHookAtTopLevel is an error in this repo.
  const staffDevicesQuery = useGetIglooStaffDevices({ userId: userIdParam }, isAvailable)
  const staffConnectionQuery = useGetIglooStaffConnection({ userId: userIdParam }, isAvailable)
  const buildingDevicesQuery = useGetIglooBuildingDevices(
    { buildingId: buildingIdParam },
    isAvailable,
  )
  const updateDevices = useUpdateIglooStaffDevices({
    buildingId: buildingIdParam,
    userId: userIdParam,
  })

  const [isSavePopupVisible, setIsSavePopupVisible] = useState(false)
  // Lazy initialisers, not inline `new Date()` in the JSX. Reading the clock during render is an
  // impure call (React Compiler flags it), and these bounds do not need to move while the form is
  // open — the backend's own rule is "future, within 365 days" of the request, not of the render.
  const [minExpiry] = useState(() => new Date())
  const [maxExpiry] = useState(() => new Date(Date.now() + MAX_CREDENTIAL_DAYS * DAY_MS))
  const [selectedDoors, setSelectedDoors] = useState<IglooBuildingDevice[]>([])
  const [expiry, setExpiry] = useState<Date | null>(null)
  // Only a user-driven change to the date may be sent. See the note on the save handler.
  const [isExpiryDirty, setIsExpiryDirty] = useState(false)
  // Set by any user edit, cleared by a save. Guards the re-seed effect — see the note there.
  const [isFormDirty, setIsFormDirty] = useState(false)

  const staffKeys = staffDevicesQuery.data?.keys ?? []
  const buildingDevices = buildingDevicesQuery.data?.devices ?? []
  const buildingDeviceIds = new Set(buildingDevices.map((device) => device.id))

  // A staff member can hold keys at several properties; this form only governs one. Splitting the
  // list keeps a key from another building out of the picker (it has no matching option and would
  // render as an unremovable ghost chip) while still telling the PM it exists.
  const keysInBuilding = staffKeys.filter((key) => buildingDeviceIds.has(key.id))
  const keysElsewhere = staffKeys.filter((key) => !buildingDeviceIds.has(key.id))

  const isConnected = staffConnectionQuery.data?.connected ?? staffKeys.length > 0

  const isLoading =
    staffDevicesQuery.isLoading || buildingDevicesQuery.isLoading || staffConnectionQuery.isLoading
  // A failed read must never be allowed to drive a write. Both reads feed the diff the backend
  // applies, and a failure resolves them to [] — see the error branch below.
  const isReadError = staffDevicesQuery.isError || buildingDevicesQuery.isError
  // The picker's option list. With no options the only submittable value is [], which the backend
  // reads as "revoke everything at this property".
  const hasAssignableDoors = buildingDevices.length > 0
  // Assigning a door mints it regardless of whether Connect was ever clicked — PUT
  // .../staff/:userId/devices creates the Igloo user on first mint the same way setup does
  // (confirmed live: assigning a door to a never-provisioned staff member succeeds directly).
  // So whenever the property has doors to offer, show the picker rather than gating it behind
  // the Connect step — the alternative is exactly W2's second reported symptom: a PM must
  // connect *and* assign without navigating away or the door picker disappears again.
  const showDoorEditor = isConnected || hasAssignableDoors

  // Re-seed from the server whenever the read settles, including after a save invalidates it, so
  // the form always shows persisted truth rather than what was last typed.
  useEffect(() => {
    if (!isConnected) return
    // ...but never over unsaved edits. `staleTime` is 0 app-wide (tanstack-query-provider
    // .constants.ts) and React Query refetches on window focus, so `dataUpdatedAt` bumps on every
    // alt-tab return. Without this guard that silently reverts the door selection AND clears
    // `isExpiryDirty`, so the next save drops a real expiration change and still toasts success.
    if (isFormDirty) return
    setSelectedDoors(
      buildingDevices.filter((device) => keysInBuilding.some((k) => k.id === device.id)),
    )
    const shortest = getShortestExpiry(keysInBuilding)
    setExpiry(shortest ? new Date(shortest) : null)
    setIsExpiryDirty(false)
  }, [
    isConnected,
    isFormDirty,
    staffDevicesQuery.dataUpdatedAt,
    buildingDevicesQuery.dataUpdatedAt,
  ])

  const doorName = (id: string) => buildingDevices.find((d) => d.id === id)?.name ?? id

  const handleSave = async () => {
    setIsSavePopupVisible(false)
    const requested = selectedDoors.map((door) => door.id)
    const heldBefore = keysInBuilding.map((key) => key.id)

    try {
      const persistedDevices = await updateDevices.mutateAsync({
        devices: requested,
        // Sending expiresAt re-issues every KEPT door, minting fresh Igloo eKeys that cannot be
        // recalled and count against the property quota. Adding one door must not silently do that
        // to the others, so the field is only ever sent when the PM actually changed it.
        ...(isExpiryDirty && expiry ? { expiresAt: expiry.getTime() } : {}),
      })

      // The response is the persisted state after the diff and the only partial-failure signal the
      // 200 carries. `persisted` is what the member can actually OPEN, which is not the same as
      // what came back: chitowntrainer-sails#6346 returns a failed door in the array with
      // `held: false`, so mapping ids blindly would count a failed mint as a success.
      const persisted = getHeldDoorIds(persistedDevices)
      const explicitlyFailed = persistedDevices.filter((d) => d.failed).map((d) => d.id)
      const { failedApplies, failedRemoves } = getFailedDoorIds({
        explicitlyFailed,
        heldBefore,
        persisted,
        requested,
      })
      const failed = [...new Set([...failedApplies, ...failedRemoves])]

      if (failed.length) {
        // Prefer the backend's per-door reason where it gave one; it distinguishes cases the door
        // name alone cannot, such as a key that was issued but not recorded (access IS active).
        const reasonById = new Map(
          persistedDevices.map((d) => [d.id, getDoorFailureCopy(d.reason)]),
        )
        toastFactory.error(
          `Some doors could not be updated: ${failed
            .map((id) => {
              const reason = reasonById.get(id)
              return reason ? `${doorName(id)} (${reason})` : doorName(id)
            })
            .join(', ')}. The list below shows what actually saved.`,
        )
      } else {
        toastFactory.success('Iglooworks door access updated')
      }

      // Always re-seed from the response, never from the request — that is the whole point of the
      // backend returning post-diff state.
      setSelectedDoors(buildingDevices.filter((device) => persisted.includes(device.id)))
      setIsExpiryDirty(false)
      setIsFormDirty(false)
    } catch (error) {
      toastFactory.error(getIglooStaffErrorMessage(error))
    }
  }

  if (!isAvailable) return null

  if (isLoading) {
    return (
      <div className="information-element">
        <div className="information-element-row">
          <Loader />
        </div>
      </div>
    )
  }

  // Neither the Connect button nor the door form may render off a failed read. Both reads resolve
  // to [] on error, which makes the two destructive paths look like no-ops: the door list collapses
  // to "no doors assigned" and Confirm would PUT `devices: []` — the backend diffs that against the
  // member's REAL keys (IglooWorkService.js:301 queries by userEmail + buildingId, not by what the
  // client sent) and revokes every one of them. A 403 on the staff read would likewise blank
  // `isConnected` and offer Connect, minting keys that cannot be recalled.
  if (isReadError) {
    return (
      <div className="information-element">
        <div className="information-element-row">
          <div className="element-name">Iglooworks</div>
          <div className="element-description">
            Could not load Iglooworks access for {staffName}. Reload the page to try again — door
            access cannot be changed until this loads, because saving against a failed read would
            revoke the keys they already hold.
          </div>
        </div>
      </div>
    )
  }

  // Reached only when there is genuinely nothing to do: never connected AND the property has no
  // Iglooworks permission doors configured (W2a — there is no UI anywhere to set them, curl-only
  // today). Confirmed live: POST staff/setup against a property with no permission doors returns
  // 200 [] and writes no audit row, so a "Connect" button here would look actionable while
  // producing no observable effect. Showing an explanation instead of a dead-end button is the
  // fix, not a bigger one — see W2a in the audit for the actual missing feature.
  if (!showDoorEditor) {
    return (
      <div className="information-element">
        <div className="information-element-row">
          <div className="element-name">Iglooworks</div>
          <div className="element-description">
            This property has no Iglooworks permission doors configured, so there is nothing to
            connect {staffName} to yet.
          </div>
        </div>
      </div>
    )
  }

  const saveConfirmationText = [
    `This will update ${staffName}'s Iglooworks door access at this property to: ${
      selectedDoors.length ? selectedDoors.map((door) => door.name).join(', ') : 'no doors'
    }.`,
    isExpiryDirty && expiry
      ? `All ${keysInBuilding.length} existing door key${keysInBuilding.length === 1 ? '' : 's'} will be re-issued with the new expiration date.`
      : '',
    'Igloo keys cannot be recalled once issued. Do you wish to proceed?',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="information-element iglooworks-staff-section">
      <div className="columns">
        <div className="description-column">
          <div className="element-name element-margin-top">Iglooworks</div>

          <div className="dropdown-buttons-container">
            <div className="dropdown-buttons-container-row">
              <div className="dropdown-container">
                <div className="dropdown-header">Door</div>
                <Select
                  isClearable
                  isMulti
                  styles={saltoSpaceDropdownStyles()}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  options={buildingDevices}
                  getOptionLabel={(option: IglooBuildingDevice) => option.name}
                  getOptionValue={(option: IglooBuildingDevice) => option.id}
                  value={selectedDoors}
                  placeholder="Select Door..."
                  closeMenuOnSelect={false}
                  onChange={(options: MultiValue<IglooBuildingDevice>) => {
                    setSelectedDoors([...options])
                    setIsFormDirty(true)
                  }}
                />
              </div>
              <div className="dropdown-container">
                <div className="dropdown-header">Expiration Date</div>
                <DatePicker
                  isClearable
                  className="dropdown-button"
                  placeholderText="MM/DD/YYYY"
                  selected={expiry}
                  minDate={minExpiry}
                  maxDate={maxExpiry}
                  onChange={(date: Date | null) => {
                    setExpiry(date)
                    setIsExpiryDirty(true)
                    setIsFormDirty(true)
                  }}
                />
              </div>
            </div>
          </div>

          {keysElsewhere.length > 0 && (
            <div className="element-description">
              This user also holds {keysElsewhere.length} Iglooworks key
              {keysElsewhere.length === 1 ? '' : 's'} at other properties. Saving here only changes
              this one.
            </div>
          )}

          {/* Two distinct [] meanings (W2 / W5 — the backend cannot tell these apart itself,
              GET .../devices returns a bare array either way): connected with nothing assigned at
              this property, and connected but nothing at this OR any property to offer. There is no
              separate "Connect" step (EL-13022): assigning doors and confirming provisions the
              Igloo user directly, so the door picker + Confirm are the whole flow. */}
          {isConnected && hasAssignableDoors && keysInBuilding.length === 0 && (
            <div className="element-description">
              Connected to Iglooworks. No doors assigned at this property yet.
            </div>
          )}

          {!hasAssignableDoors && (
            <div className="element-description">
              This property has no Iglooworks permission doors configured, so there is nothing to
              assign yet.
            </div>
          )}

          <div className="dropdown-buttons-container-row">
            <button
              type="button"
              className={`confirm-button button-primary button ${updateDevices.isPending ? 'is-loading' : ''}`}
              // With an empty option list the only value Confirm can submit is [], which the
              // backend reads as "revoke every key this member holds here" — never a deliberate
              // action from a picker that had nothing in it.
              disabled={updateDevices.isPending || !hasAssignableDoors}
              onClick={() => setIsSavePopupVisible(true)}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>

      <ConfirmPopup
        title="Caution"
        body={saveConfirmationText}
        isOpen={isSavePopupVisible}
        cancelText="Close"
        confirmText="Yes, confirm"
        cancelCallback={() => setIsSavePopupVisible(false)}
        confirmCallback={handleSave}
        className="is-caution"
        isLoading={updateDevices.isPending}
      />
    </div>
  )
}

export default IglooWorkStaffSection
